import React from 'react';
import BaseBlock from './BaseBlock';
import { VaultRefsLine } from './VaultRefsLine';

/**
 * EducationBlock renders educational entries (Degree, University, GPA, Date).
 * Supports: classic, timeline, compact, card, sidebar variants.
 */
export const EducationBlock = ({ items = [], variant = 'classic' }) => {
  if (!items || items.length === 0) return null;

  // 1. Compact Layout (Compressed single-line rows)
  if (variant === 'compact') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {items.map((edu, idx) => {
          const schoolName = edu.school || edu.college || edu.university || edu.institution || '';
          return (
            <div 
              key={idx} 
              style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'baseline', 
                fontSize: 'var(--font-size-body, 9pt)',
                borderBottom: '1px dashed var(--color-border, #E2E8F0)',
                paddingBottom: '4px',
                width: '100%' 
              }}
            >
              <div>
                <strong style={{ color: 'var(--color-text-main, #2D3748)' }}>{edu.degree}</strong>
                <span style={{ color: 'var(--color-text-light, #718096)', margin: '0 8px' }}>|</span>
                <span style={{ color: 'var(--color-secondary, #4A5568)' }}>{schoolName}</span>
                {(edu.gpa || edu.cgpa) && (
                  <>
                    <span style={{ color: 'var(--color-text-light, #718096)', margin: '0 8px' }}>|</span>
                    <span style={{ fontSize: 'var(--font-size-meta, 8pt)', color: 'var(--color-text-light, #718096)' }}>GPA: {edu.gpa || edu.cgpa}</span>
                  </>
                )}
              </div>
              <span style={{ fontSize: 'var(--font-size-meta, 8pt)', color: 'var(--color-text-light, #718096)', whiteSpace: 'nowrap' }}>
                {edu.duration || edu.date || edu.year || ''}
              </span>
            </div>
          );
        })}
      </div>
    );
  }

  // 2. Card Layout (Outlined card container)
  if (variant === 'card') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {items.map((edu, idx) => {
          const schoolName = edu.school || edu.college || edu.university || edu.institution || '';
          return (
            <div 
              key={idx} 
              style={{ 
                border: '1px solid var(--color-border, #E2E8F0)', 
                borderRadius: '6px', 
                padding: '12px', 
                backgroundColor: 'var(--color-bg-page, #FFFFFF)',
                width: '100%'
              }}
            >
              <BaseBlock
                title={edu.degree || ''}
                rightMeta={edu.duration || edu.date || edu.year || ''}
                subtitle={schoolName}
                subRightMeta={edu.gpa || edu.cgpa ? `CGPA: ${edu.gpa || edu.cgpa}` : ''}
                bullets={edu.bullets || []}
              />
            </div>
          );
        })}
      </div>
    );
  }

  // 3. Timeline Layout (Vertical left-timeline connector)
  if (variant === 'timeline') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', borderLeft: '2px solid var(--color-border, #E2E8F0)', paddingLeft: '16px', marginLeft: '6px', gap: 'var(--spacing-item-mb, 12px)' }}>
        {items.map((edu, idx) => {
          const schoolName = edu.school || edu.college || edu.university || edu.institution || '';
          return (
            <div key={idx} style={{ position: 'relative', width: '100%' }}>
              <div 
                style={{ 
                  position: 'absolute', 
                  left: '-23px', 
                  top: '4px', 
                  width: '12px', 
                  height: '12px', 
                  borderRadius: '50%', 
                  backgroundColor: 'var(--color-primary, #16324F)',
                  border: '2px solid var(--color-bg-page, #FFFFFF)'
                }} 
              />
              <BaseBlock
                title={edu.degree || ''}
                rightMeta={edu.duration || edu.date || edu.year || ''}
                subtitle={schoolName}
                subRightMeta={edu.gpa || edu.cgpa ? `CGPA: ${edu.gpa || edu.cgpa}` : ''}
                bullets={edu.bullets || []}
              />
              <VaultRefsLine refs={edu.vault_refs} />
            </div>
          );
        })}
      </div>
    );
  }

  // 4. Sidebar Layout (Vertically stacked block for columns)
  if (variant === 'sidebar') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {items.map((edu, idx) => {
          const schoolName = edu.school || edu.college || edu.university || edu.institution || '';
          return (
            <div key={idx} style={{ fontSize: 'var(--font-size-body, 9pt)', lineHeight: '1.4', color: 'var(--color-text-main)' }}>
              <strong style={{ color: 'var(--color-primary, #16324F)', display: 'block' }}>{edu.degree}</strong>
              <span style={{ color: 'var(--color-text-main, #334155)', display: 'block', fontSize: 'var(--font-size-meta, 8pt)' }}>{schoolName}</span>
              <span style={{ color: 'var(--color-text-light, #718096)', display: 'block', fontSize: 'var(--font-size-meta, 8pt)' }}>
                {edu.duration || edu.date || edu.year}
              </span>
              {(edu.gpa || edu.cgpa) && (
                <span style={{ color: 'var(--color-text-light, #718096)', display: 'block', fontSize: 'var(--font-size-meta, 8pt)', fontWeight: 'bold' }}>
                  GPA: {edu.gpa || edu.cgpa}
                </span>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  // 5. Classic Layout / Fallback
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-item-mb, 12px)' }}>
      {items.map((edu, idx) => {
        const schoolName = edu.school || edu.college || edu.university || edu.institution || '';
        return (
          <div key={idx}>
            <BaseBlock
              title={edu.degree || edu.degreeName || ''}
              rightMeta={edu.duration || edu.date || edu.year || ''}
              subtitle={schoolName}
              subRightMeta={edu.gpa || edu.cgpa ? `CGPA: ${edu.gpa || edu.cgpa}` : ''}
              bullets={edu.bullets || []}
            />
            <VaultRefsLine refs={edu.vault_refs} />
          </div>
        );
      })}
    </div>
  );
};
export default EducationBlock;
